import { config } from "../../config/config.js";
import { classes } from "polytype";
import assertObjectFound from "../../base/assertObjectFound.js";
import { type AsyncResourceVariant, provideReact } from "../../react/index.js";
import type {
  ContributorData,
  ContributorImprint,
  ContributorListItemData,
  ContributorListQueryData,
  OwnContributorData,
} from "./types.js";
import { Customer } from "../../customer/index.js";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base/index.js";
import type { ContributorExtensionListQuery } from "../ContributorExtension/index.js";
import { ContributorExtension } from "../ContributorExtension/index.js";
import { File } from "../../file/index.js";

export class Contributor extends ReferenceModel {
  public readonly ownExtensions: ContributorExtensionListQuery;

  public constructor(id: string) {
    super(id);
    this.ownExtensions = ContributorExtension.query(this);
  }
  public static ofId(id: string) {
    return new Contributor(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.contributor.find(id);
    if (data !== undefined) {
      return new ContributorDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const contributor = await this.find(id);
    assertObjectFound(contributor, "contributor", id);
    return contributor;
  });

  public findDetailed = provideReact(
    () => Contributor.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ContributorDetailed | undefined>>;

  public getDetailed = provideReact(
    () => Contributor.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ContributorDetailed>>;

  public query(query: ContributorListQueryData = {}) {
    return new ContributorListQuery(query);
  }
}

class ContributorCommon extends classes(
  DataModel<ContributorData | OwnContributorData | ContributorListItemData>,
  Contributor,
) {
  public readonly customer: Customer;
  public readonly name: string;
  public readonly description?: string;
  public readonly email?: string;
  public readonly phone?: string;
  public readonly url?: string;
  public readonly logo?: File;
  public readonly imprint?: ContributorImprint;

  public constructor(
    data: ContributorData | OwnContributorData | ContributorListItemData,
  ) {
    super([data], [data.id]);
    this.customer = Customer.ofId(data.customerId);
    this.name = data.name;
    this.description = data.description;
    this.email = data.email;
    this.phone = data.phone;
    this.url = data.url;
    this.logo = data.logoRefId ? File.ofId(data.logoRefId) : undefined;
    this.imprint = data.imprint;
  }
}

export class ContributorDetailed extends classes(
  ContributorCommon,
  DataModel<ContributorData | OwnContributorData>,
) {
  public constructor(data: ContributorData) {
    super([data], [data]);
  }
}

export class ContributorListItem extends classes(
  ContributorCommon,
  DataModel<ContributorListItemData>,
) {
  public constructor(data: ContributorListItemData) {
    super([data], [data]);
  }
}

export class ContributorListQuery extends ListQueryModel<ContributorListQueryData> {
  public constructor(query: ContributorListQueryData = {}) {
    super(query);
  }

  public refine(query: ContributorListQueryData) {
    return new ContributorListQuery({
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.contributor.list(
      this.query,
    );

    return new ContributorList(
      this.query,
      items.map((d) => new ContributorListItem(d)),
      totalCount,
    );
  }, [this.queryId, "contributor"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "contributor"]);
}

export class ContributorList extends classes(
  ContributorListQuery,
  ListDataModel<ContributorListItem>,
) {
  public constructor(
    query: ContributorListQueryData,
    contributors: ContributorListItem[],
    totalCount: number,
  ) {
    super([query], [contributors, totalCount]);
  }
}
